import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowLeft, ArrowUp, Check, ChevronDown, Copy, CopyPlus, MessageCircle, MoreHorizontal, Pause, Pencil, Play, Plus, RefreshCw, Sparkles, Target, Trash2, X } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, classesBotao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Estado } from '../../componentes/estados';
import { BuscaRemota, CampoData, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { LinkParceiro } from '../../componentes/link-parceiro';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { EdicaoRapida } from '../../componentes/edicao-rapida';
import { useToast } from '../../componentes/toast';
import { api, del, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, email as emailFmt, nomeParceiro, nomePessoa, telefoneFormatado, textoLivre } from '../../nucleo/formato';
import { rolarAbaAtiva } from '../../nucleo/foco';
import { useSessao } from '../../nucleo/sessao';
import { ModalGanhar, ModalPausa, ModalPerder } from './Encerrar';
import { FormTarefa, rascunhoInicial } from './FormTarefa';
import { AbaArquivos, AbaEmail, AbaQuestionarios, TarefasDaNegociacao, useAtividadesDaNegociacao } from './FichaAbas';
import { useListas } from './crm-listas';
import { EmpresasAdicionais } from './FichaEmpresasAdicionais';
import { CamposExtrasNegociacao, CAMINHO_CONTATO_CRM, EmpresaNegociacao, InfoAdicionalContato, useFormularioParceiro, type ContatoFicha, type EmpresaFicha } from './FichaLateral';
import { CartaoVerticais } from '../../componentes/verticais';
import { AbaProdutos } from './FichaProdutos';
import { AbaPropostas } from './FichaPropostas';
import { Estrelas } from './CrmComponentes';
import { opcoesRegime, rotuloRegime } from './crm-regime';
import { CAMINHO_COMERCIAL, caminhoDaNegociacao } from './crm-caminhos';
import { edicaoDaNegociacao, podeCriarTarefaNaNegociacao } from './negociacao-edicao';
import { useVoltar } from '../../nucleo/voltar';
import type { ParametrosSistema } from '../../nucleo/texto-sistema';
import type { Oportunidade } from './tipos';
import { useContextoDaCriacao } from '../../nucleo/criacao-rapida';

/**
 * CRM no novo desenho, lote 4 (C-B, ficha 2026-10-26 "crm-novo-desenho"): a ficha da negociação em página (o RD), em
 * `/crm/comercial/negociacao/:id`. Cabeçalho com a empresa, o status e o funil; "Mais opções" (pausar ou retomar, duplicar,
 * excluir), Playbook, marcar perda e marcar venda; as etapas com os dias em cada uma (clicar move); à esquerda Negociação
 * (edição no lugar), Contatos (várias pessoas, uma principal), Empresa e Responsável; à direita a próxima ação, as próximas
 * tarefas e as abas Histórico, E-mail, Tarefas, Questionários, Produtos, Arquivos e Propostas.
 */

const ENTIDADE = 'pipeline.oportunidade';
const ABAS = ['historico', 'email', 'tarefas', 'questionarios', 'produtos', 'arquivos', 'propostas'] as const;
type Aba = (typeof ABAS)[number];
/** L6 (C19): a aba pedida no endereço (`?aba=tarefas`); sem pedido válido, o Histórico. Função pura, testada. */
export const abaDaBusca = (valor: string | null): Aba => ABAS.find((x) => x === valor) ?? 'historico';
/**
 * Lista de 30/10 (B), item 21: o evento "Copiada para" do histórico abre a cópia (o id vem nos parâmetros do servidor).
 * Função pura, testada.
 */
export function linkDoHistoricoNegociacao(p: ParametrosSistema): string | null {
  return p.modelo === 'negociacaoCopiadaPara' && typeof p.oportunidade_id === 'string' ? caminhoDaNegociacao(p.oportunidade_id) : null;
}
/** Rodada crm1 (VIS-M4): as abas da ficha pelo `Abas` do núcleo (setas, Home e End, roving tabindex, `aria-controls`). */
const ID_ABAS = 'ficha-negociacao';

interface Ficha {
  etapas: { id: string; nome: string; tipo: 'aberta' | 'ganha' | 'perdida'; ordem: number; dias: number | null; playbook: { objetivo?: string; descricao?: string; itens?: string[] } }[];
  contatos: ContatoFicha[];
  playbook_marcados: Record<string, number[]>;
  ultimo_contato: string | null;
  interacoes: number;
  empresa: EmpresaFicha | null;
  /** Lista de 03/11, item 3: as empresas adicionais (a principal é `empresa`). Servidor antigo: ausente. */
  empresas_adicionais?: { parceiro_id: string; nome: string }[];
}

/**
 * Ajustes do CRM (27/10): `itensMenu` acrescenta itens ao "Mais opções" (a junção das trilhas liga aqui o "Dê seu feedback",
 * decisão 5), depois de pausar, apagar e duplicar.
 */
export function FichaNegociacao({ itensMenu }: { itensMenu?: ItemMenu[] } = {}) {
  const { t } = useTranslation('pipeline');
  const { id = '' } = useParams();
  const op = useQuery({ queryKey: ['pipeline', 'oportunidade', id], queryFn: () => get<Oportunidade>(`/pipeline/oportunidades/${id}`) });
  const ficha = useQuery({ queryKey: ['pipeline', 'crm', 'ficha', id], queryFn: () => get<Ficha>(`/pipeline/crm/negociacoes/${id}/ficha`) });
  return (
    <Estado consulta={op}>
      {(o) => (ficha.data ? <Pagina o={o} f={ficha.data} itensExtrasMenu={itensMenu} /> : <p className="text-sm text-tinta-3">{t('crm.ficha.carregando')}</p>)}
    </Estado>
  );
}

function Pagina({ o, f, itensExtrasMenu }: { o: Oportunidade; f: Ficha; itensExtrasMenu?: ItemMenu[] }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  // Rodada rrl-1 (SPEC-M1): o botão flutuante cria reunião e tarefa ligadas a esta negociação.
  useContextoDaCriacao({ negociacao: { id: o.id, parceiro_id: o.parceiro_id } });
  const listas = useListas();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  // Lista de 30/10 B (item 6): o Voltar volta à tela de onde a pessoa veio; sem ela no app, ao Comercial.
  const voltar = useVoltar(CAMINHO_COMERCIAL).voltar;
  const [busca, setBusca] = useSearchParams();
  const aba: Aba = abaDaBusca(busca.get('aba'));
  const mudarAba = (a: Aba) => setBusca((b) => (a === 'historico' ? (b.delete('aba'), b) : (b.set('aba', a), b)), { replace: true });
  // A aba ativa entra na vista da faixa que rola (só o `scrollLeft`, regra r34).
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-abas="${ID_ABAS}"] [role="tab"][aria-selected="true"]`);
    if (el) rolarAbaAtiva(el, 'perto');
  }, [aba]);
  const [encerrar, setEncerrar] = useState<'ganhar' | 'perder' | null>(null);
  const [playbook, setPlaybook] = useState(false);
  // Lista de 30/10 (B), item 9: pausar e retomar pedem o motivo num pop-up (`ModalPausa`).
  const [pausando, setPausando] = useState<'pausar' | 'retomar' | null>(null);
  const [excluir, setExcluir] = useState(false);
  const [copiar, setCopiar] = useState(false);
  const [criarTarefa, setCriarTarefa] = useState(false);
  // Lista de 30/10 (B), item 7.1: o "pode editar?" da negociação num ponto só (`negociacao-edicao.ts`; a trava da pausa entra lá).
  const edicao = edicaoDaNegociacao(o, s.pode('pipeline.editar'));
  const pode = edicao.atividades;
  // Item 8: a pausa trava a edição, mas quem edita ainda retoma (e pausa) a negociação.
  const podePausar = s.pode('pipeline.editar');
  const podeDados = edicao.dados;
  // Val5 (29/10): marcar venda ou perda e reabrir têm ação própria (`pipeline.ganhar_perder`).
  const podeEncerrar = s.pode('pipeline.ganhar_perder');
  const aberta = o.estado === 'aberta';
  const pausada = !!o.pausada_em && aberta;
  const atividades = useAtividadesDaNegociacao(o.id);
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['pipeline'] });
    await qc.invalidateQueries({ queryKey: ['chatter', ENTIDADE, o.id] });
    // A ficha do contato e a coluna da lista mostram quem participa: mudar o contato aqui atualiza as duas (rcn-1, ROB-B2).
    await qc.invalidateQueries({ queryKey: ['crm'] });
    await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
  };
  const acao = useMutation({
    mutationFn: async (f: () => Promise<unknown>) => f(),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const status = o.estado === 'ganha' ? 'vendido' : o.estado === 'perdida' ? 'perdido' : pausada ? 'pausado' : 'andamento';
  const corStatus = { vendido: 'verde', perdido: 'laranja', pausado: 'neutro', andamento: 'azul' } as const;
  const atual = f.etapas.find((e) => e.id === o.etapa_id);
  const contatoPrincipal = f.contatos.find((c) => c.principal) ?? f.contatos[0] ?? null;
  const pendentes = (atividades.data?.itens ?? []).filter((a) => a.estado === 'pendente').sort((a, b) => a.data_hora.localeCompare(b.data_hora));
  // Lista de 30/10 (B), item 21: a bandeirola da criação diz de qual negociação esta é cópia (com o link, se quem lê a vê).
  const marcoDaCriacao = o.copiada_de_id
    ? {
        rotulo: o.copiada_de_titulo ? t('crm.copia.marco') : t('crm.copia.marcoSemLink'),
        data: o.created_at,
        link: o.copiada_de_titulo ? { para: caminhoDaNegociacao(o.copiada_de_id), rotulo: textoLivre(o.copiada_de_titulo, o.parceiro_nome) } : null,
      }
    : { rotulo: t('crm.ficha2.criada'), data: o.created_at };
  const recomendacao = !aberta
    ? null
    : pendentes[0]
      ? t('crm.ficha.recomendaConcluir', { titulo: pendentes[0].titulo, data: dataHora(pendentes[0].data_hora) })
      : t('crm.ficha.recomendaAgendar');

  return (
    <div className="flex min-w-0 flex-col gap-5" data-ficha-negociacao={o.id}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-2">
          <Botao variante="fantasma" aria-label={t('crm.ficha.voltar')} icone={<ArrowLeft className="size-4" />} onClick={voltar} />
          <div className="min-w-0">
            {o.parceiro_nome && <p className="kicker">{nomeParceiro(o.parceiro_nome)}</p>}
            <h1 className="mt-1 text-2xl font-bold tracking-tight break-words">{textoLivre(o.titulo, o.parceiro_nome)}</h1>
            <p className="mt-1 flex flex-wrap gap-2">
              <Selo cor={corStatus[status]}>{t(`crm.comercial.statusNeg.${status}`)}</Selo>
              <Selo>{o.funil_nome}</Selo>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Ajustes do CRM (27/10, F11): "Mais opções" e Playbook só com o ícone, como no RD; o menu na ordem do RD (pausar,
              apagar, duplicar) e com os ícones. */}
          <Menu
            rotulo={t('crm.ficha2.maisOpcoes')}
            classeGatilho={classesBotao('secundario', 'md')}
            gatilho={<MoreHorizontal className="size-4" aria-hidden />}
            itens={[
              ...(aberta && podePausar ? [{ chave: 'pausar', rotulo: pausada ? t('crm.ficha.retomar') : t('crm.ficha.pausar'), icone: pausada ? <Play className="size-4" aria-hidden /> : <Pause className="size-4" aria-hidden />, aoClicar: () => setPausando(pausada ? 'retomar' : 'pausar') }] : []),
              ...(s.pode('pipeline.excluir') && o.estado !== 'ganha' ? [{ chave: 'excluir', rotulo: t('crm.ficha2.apagar'), icone: <Trash2 className="size-4" aria-hidden />, aoClicar: () => setExcluir(true) }] : []),
              ...(s.pode('pipeline.criar')
                ? [
                    // Lista de 30/10 (B), item 21: "Copiar negociação" confirma o que vai e o que fica na original.
                    { chave: 'duplicar', rotulo: t('crm.copia.menu'), icone: <CopyPlus className="size-4" aria-hidden />, aoClicar: () => setCopiar(true) },
                  ]
                : []),
              ...(itensExtrasMenu ?? []),
            ]}
          />
          <Botao aria-label={t('crm.ficha2.playbook')} title={t('crm.ficha2.playbook')} icone={<Target className="size-4" />} onClick={() => setPlaybook(true)} data-abrir-playbook="" />
          {pausada && podePausar && (
            <Botao variante="primario" icone={<Play className="size-4" />} onClick={() => setPausando('retomar')} data-retomar="">
              {t('crm.ficha.retomar')}
            </Botao>
          )}
          {/* Ajustes do CRM (27/10, F9, decisão 15): a encerrada reabre (a ganha com contrato vivo é recusada com o motivo). */}
          {!aberta && podeEncerrar && (
            <Botao
              icone={<RefreshCw className="size-4" />}
              data-reabrir=""
              onClick={() =>
                acao.mutate(async () => {
                  await post(`/pipeline/oportunidades/${o.id}/reabrir`);
                  toast.sucesso(t('crm.ficha2.reaberta'));
                })
              }
            >
              {t('crm.ficha2.reabrir')}
            </Botao>
          )}
          {aberta && podeEncerrar && !pausada && (
            <>
              <Botao icone={<ArrowDown className="size-4" />} onClick={() => setEncerrar('perder')} data-marcar-perda="">
                {t('crm.ficha.marcarPerda')}
              </Botao>
              <Botao variante="primario" icone={<ArrowUp className="size-4" />} onClick={() => setEncerrar('ganhar')} data-marcar-venda="">
                {t('crm.ficha.marcarVenda')}
              </Botao>
            </>
          )}
        </div>
      </header>

      {pausada && (
        <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2" role="status" data-aviso-pausada="">
          {t('crm.pausa.aviso')}
        </p>
      )}

      {/* Ajustes do CRM (27/10, F10): a trilha em setas do RD (atual no acento, as anteriores marcadas, os dias no título). */}
      <ol
        className="flex min-w-0 overflow-x-auto pb-1"
        aria-label={t('crm.ficha.etapas')}
        data-etapas-negociacao=""
        // Rodada crm1 (VIS-B2): sem etapa clicável (fechada ou sem permissão), a faixa que rola vira região focável (regra r35).
        {...(!aberta || !pode ? { role: 'region', tabIndex: 0 } : {})}
      >
        {f.etapas
          .filter((e) => e.tipo === 'aberta' || e.id === o.etapa_id)
          .map((e) => {
            const eAtual = e.id === o.etapa_id;
            const passou = atual && e.ordem < atual.ordem;
            const dias = e.dias !== null ? t('crm.ficha.dias', { count: e.dias }) : null;
            return (
              <li key={e.id} className="etapa-seta-item relative h-10 min-w-36 flex-1" data-passou={passou ? '' : undefined}>
                <span aria-hidden className={`etapa-seta absolute inset-0 ${eAtual ? 'bg-acento-forte' : passou ? 'bg-acento-suave' : 'bg-sup-3'}`} />
                <button
                  type="button"
                  aria-current={eAtual ? 'step' : undefined}
                  disabled={!aberta || !pode || eAtual || e.tipo !== 'aberta'}
                  data-etapa-ficha={e.nome}
                  title={dias ? t('crm.ficha2.diasEtapa', { nome: e.nome, dias }) : e.nome}
                  onClick={() => acao.mutate(() => post(`/pipeline/oportunidades/${o.id}/mover`, { etapa_id: e.id }))}
                  className={`relative flex h-full w-full items-center justify-center gap-1 rounded-md px-5 text-center text-sm font-semibold focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco ${eAtual ? 'text-sup-1' : 'text-tinta-1'} enabled:hover:underline`}
                >
                  <span className="truncate" title={e.nome}>
                    {e.nome}
                  </span>
                  {dias && <span className="shrink-0 text-xs font-normal">({dias})</span>}
                </button>
              </li>
            );
          })}
      </ol>

      <div className="grid min-w-0 gap-5 lg:grid-cols-3">
        <aside className="flex min-w-0 flex-col gap-3 lg:col-span-1">
          <Secao titulo={t('crm.ficha.secoes.negociacao')}>
            <CampoNegociacao o={o} rotulo={t('crm.ficha.campos.nome')} valor={o.titulo} tipo="texto" podeEditar={podeDados} salvar={(v) => patch(`/pipeline/oportunidades/${o.id}`, { titulo: v })} aoSalvar={recarregar} />
            <div className="flex min-h-11 items-center justify-between gap-3 border-b border-borda px-2 py-2 text-sm">
              <span className="text-tinta-3">{t('crm.comercial.qualificacao')}</span>
              <Estrelas valor={o.qualificacao ?? 0} rotulo={t('crm.comercial.estrelas', { count: o.qualificacao ?? 0 })} aoMudar={pode ? (v) => acao.mutate(() => post(`/pipeline/oportunidades/${o.id}/qualificar`, { qualificacao: v })) : undefined} />
            </div>
            {aberta ? (
              <CampoNegociacao o={o} rotulo={t('crm.ficha.campos.previsao')} valor={o.previsao_fechamento ?? ''} mostrar={o.previsao_fechamento ? data(`${o.previsao_fechamento}T12:00`) : ''} tipo="data" podeEditar={podeDados} salvar={(v) => patch(`/pipeline/oportunidades/${o.id}`, { previsao_fechamento: v || null })} aoSalvar={recarregar} />
            ) : (
              <Linha rotulo={t('crm.ficha.campos.fechamento')}>{dataHora(o.ganha_em ?? o.perdida_em)}</Linha>
            )}
            {/* Lote crm5 (E10, decisão C): regime tributário pretendido, editável aqui como os vizinhos (mesmas opções do Cadastros). */}
            <CampoNegociacao o={o} rotulo={t('campos.regimePretendido')} valor={o.regime_pretendido ?? ''} mostrar={rotuloRegime(o.regime_pretendido)} tipo="texto" selecao={opcoesRegime()} podeEditar={podeDados} salvar={(v) => patch(`/pipeline/oportunidades/${o.id}`, { regime_pretendido: v || null })} aoSalvar={recarregar} />
            <CampoNegociacao o={o} rotulo={t('crm.filtros.fonte')} valor={o.utm_fonte ?? ''} tipo="texto" opcoes={listas.data?.fontes.map((x) => x.nome)} livre={listas.data?.fontes_livres ?? true} podeEditar={podeDados} salvar={(v) => patch(`/pipeline/oportunidades/${o.id}`, { utm_fonte: v || null })} aoSalvar={recarregar} />
            <CampoNegociacao o={o} rotulo={t('crm.filtros.campanha')} valor={o.utm_campanha ?? ''} mostrar={o.utm_campanha || t('crm.ficha.semCampanha')} tipo="texto" opcoes={listas.data?.campanhas.map((x) => x.nome)} livre={listas.data?.campanhas_livres ?? true} podeEditar={podeDados} salvar={(v) => patch(`/pipeline/oportunidades/${o.id}`, { utm_campanha: v || null })} aoSalvar={recarregar} />
            <Linha rotulo={t('crm.ficha.campos.criada')}>{dataHora(o.created_at)}</Linha>
            <Linha rotulo={t('crm.ficha.campos.valor')}>
              <span data-valor-negociacao="">{dinheiro(o.valor_centavos, o.moeda)}</span>
            </Linha>
            <Linha rotulo={t('crm.ficha.campos.ultimoContato')}>{f.ultimo_contato ? dataHora(f.ultimo_contato) : t('crm.ficha.semContato')}</Linha>
            {o.motivo_perda_nome && <Linha rotulo={t('campos.motivo')}>{textoLivre(o.motivo_perda_nome)}</Linha>}
            <CamposExtrasNegociacao o={o} podeEditar={podeDados} aoSalvar={recarregar} />
          </Secao>
          <Secao titulo={t('crm.ficha.secoes.contatos')}>
            <Contatos o={o} f={f} podeEditar={podeDados} aoMudar={recarregar} />
          </Secao>
          <Secao titulo={t('crm.ficha.secoes.empresa')}>
            <EmpresaNegociacao o={o} empresa={f.empresa} podeEditar={podeDados} aoMudar={recarregar} comAdicionais={(f.empresas_adicionais ?? []).length > 0} />
            <EmpresasAdicionais o={o} lista={f.empresas_adicionais ?? []} podeEditar={podeDados} aoMudar={recarregar} />
          </Secao>
          {/* Ajustes do CRM (27/10, F7, decisão 14): a vertical da negociação (as empresas do grupo em que é trabalhada). */}
          <CartaoVerticais entidade={ENTIDADE} registroId={o.id} podeEditar={podeDados} />
          <Secao titulo={t('crm.ficha.secoes.responsavel')}>
            <Responsavel o={o} podeEditar={podeDados} aoSalvar={recarregar} />
          </Secao>
        </aside>

        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          {recomendacao && (
            <section className="rounded-xl border border-borda bg-sup-1 p-4 text-sm" data-proxima-acao="">
              <h2 className="flex items-center gap-2 font-semibold">
                <Sparkles className="size-4 text-acento-forte" aria-hidden />
                {t('crm.ficha.proximaAcao')}
              </h2>
              <p className="mt-1 text-tinta-2">{recomendacao}</p>
            </section>
          )}
          <TarefasDaNegociacao op={o} podeEditar={pode} compacto />
          <Abas<Aba> idBase={ID_ABAS} grupo={ID_ABAS} rotulo={t('crm.ficha.abasRotulo')} ativa={aba} aoMudar={mudarAba} itens={ABAS.map((a) => ({ chave: a, rotulo: t(`crm.ficha.abas.${a}`) }))} />
          <PainelAba idBase={ID_ABAS} chave={aba}>
            {/* Ajustes do CRM (27/10, F3): o histórico em linha do tempo do RD, com o marco da criação. */}
            {aba === 'historico' && <Chatter variante="linha" criarTarefa={podeCriarTarefaNaNegociacao(o, s.pode('pipeline.criar')) ? () => setCriarTarefa(true) : null} marco={marcoDaCriacao} herdado={o.copiada_de_id && o.copiada_de_titulo ? { entidade: ENTIDADE, registroId: o.copiada_de_id, ate: o.created_at } : null} linkDoSistema={linkDoHistoricoNegociacao} entidade={ENTIDADE} registroId={o.id} nomes={[o.parceiro_nome, ...f.contatos.map((c) => c.nome), ...(f.empresas_adicionais ?? []).map((c) => c.nome)]} titulo={o.titulo} />}
            {/* Lista de 02/11, item 6: o mesmo formulário do "Criar tarefa" de Próximas tarefas. */}
            {criarTarefa && <FormTarefa travarNegociacao inicial={rascunhoInicial(o.responsavel_id ?? s.usuario.id, { parceiroId: o.parceiro_id, parceiroNome: o.parceiro_nome, oportunidadeId: o.id, oportunidadeTitulo: o.titulo })} aoFechar={() => setCriarTarefa(false)} />}
            {aba === 'email' && <AbaEmail op={o} podeEditar={pode} />}
            {aba === 'tarefas' && <TarefasDaNegociacao op={o} podeEditar={pode} />}
            {aba === 'questionarios' && <AbaQuestionarios op={o} podeEditar={pode} />}
            {aba === 'produtos' && <AbaProdutos op={o} podeEditar={pode} />}
            {aba === 'arquivos' && <AbaArquivos op={o} podeEditar={pode} contatos={[...f.contatos.map((c) => c.nome), ...(f.empresas_adicionais ?? []).map((c) => c.nome)]} />}
            {aba === 'propostas' && <AbaPropostas op={o} contato={contatoPrincipal?.nome ?? null} podeEditar={pode} />}
          </PainelAba>
        </div>
      </div>

      {encerrar === 'ganhar' && <ModalGanhar completo oportunidade={o} aoFechar={() => setEncerrar(null)} aoConcluir={() => void recarregar().then(() => setEncerrar(null))} />}
      {encerrar === 'perder' && <ModalPerder oportunidade={o} aoFechar={() => setEncerrar(null)} aoConcluir={() => void recarregar().then(() => setEncerrar(null))} />}
      {pausando && <ModalPausa oportunidade={o} retomar={pausando === 'retomar'} aoFechar={() => setPausando(null)} aoConcluir={() => void recarregar().then(() => setPausando(null))} />}
      {playbook && <Playbook o={o} f={f} podeEditar={podeDados} aoFechar={() => setPlaybook(false)} />}
      <Confirmar
        aberto={copiar}
        aoFechar={() => setCopiar(false)}
        titulo={t('crm.copia.confirmarTitulo')}
        texto={t('crm.copia.confirmarTexto')}
        carregando={acao.isPending}
        aoConfirmar={() =>
          acao.mutate(async () => {
            const nova = crypto.randomUUID();
            await post(`/pipeline/oportunidades/${o.id}/duplicar`, { nova_id: nova });
            setCopiar(false);
            toast.sucesso(t('crm.copia.copiada'));
            navegar(caminhoDaNegociacao(nova));
          })
        }
      />
      <Confirmar
        aberto={excluir}
        aoFechar={() => setExcluir(false)}
        perigoso
        titulo={t('crm.ficha.excluirTitulo')}
        texto={t('crm.ficha.excluirTexto', { titulo: o.titulo })}
        aoConfirmar={() =>
          acao.mutate(async () => {
            await del(`/pipeline/oportunidades/${o.id}`);
            setExcluir(false);
            toast.sucesso(t('crm.ficha.excluida'));
            navegar(CAMINHO_COMERCIAL);
          })
        }
      />
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <details open className="group rounded-xl border border-borda bg-sup-1" data-secao-ficha={titulo}>
      <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold">
        {titulo}
        <ChevronDown className="size-4 transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <div className="flex flex-col border-t border-borda px-4 py-2">{children}</div>
    </details>
  );
}

/** Linha só de leitura, no mesmo desenho da linha de edição rápida (`EdicaoRapida`), como no perfil da empresa. */
function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex min-h-11 items-center justify-between gap-3 border-b border-borda px-2 py-2 text-sm last:border-0">
      <span className="text-tinta-3">{rotulo}</span>
      <span className="min-w-0 truncate text-right font-medium text-tinta-1" title={typeof children === 'string' ? children : undefined}>{children}</span>
    </div>
  );
}

/**
 * Campo da negociação com edição no lugar. Lista de 30/10 (B), item 7.1: a mesma edição rápida do perfil da empresa do CRM
 * (`EdicaoRapida`): a linha inteira abre o editor ali mesmo, Enter salva, Esc cancela e o erro do servidor fica no campo.
 */
function CampoNegociacao({
  o,
  rotulo,
  valor,
  mostrar,
  tipo,
  opcoes,
  livre = true,
  selecao,
  podeEditar,
  salvar,
  aoSalvar,
}: {
  o: Oportunidade;
  rotulo: string;
  valor: string;
  mostrar?: string;
  tipo: 'texto' | 'data';
  /** Lote 5: opções das Configurações do CRM (fontes, campanhas); com `livre`, só sugestões. */
  opcoes?: string[];
  livre?: boolean;
  /** Lote crm5 (E10): opções fechadas com rótulo próprio (o regime tributário); o valor gravado é o código. */
  selecao?: { valor: string; rotulo: string }[];
  podeEditar: boolean;
  salvar: (v: string) => Promise<unknown>;
  aoSalvar: () => Promise<void>;
}) {
  const [v, setV] = useState(valor);
  return (
    <EdicaoRapida
      rotulo={rotulo}
      exibir={textoLivre(mostrar ?? valor, o.parceiro_nome)}
      pode={podeEditar}
      aoAbrir={() => setV(valor)}
      aoSalvar={async () => {
        await salvar(v.trim());
        await aoSalvar();
      }}
      data-campo-ficha={rotulo}
    >
      {({ id, erro }) => (
        <Rotulo id={id} rotulo={rotulo} erro={erro || undefined}>
          {tipo === 'data' ? (
            <CampoData id={id} type="date" value={v} aria-invalid={!!erro} onChange={(e) => setV(e.target.value)} />
          ) : selecao ? (
            <Selecao id={id} value={v} aria-invalid={!!erro} opcoes={[{ valor: '', rotulo: '' }, ...selecao]} onChange={(e) => setV(e.target.value)} />
          ) : opcoes?.length && !livre ? (
            <Selecao id={id} value={v} aria-invalid={!!erro} opcoes={[{ valor: '', rotulo: '' }, ...[...new Set([...opcoes, ...(valor ? [valor] : [])])].map((x) => ({ valor: x, rotulo: x }))]} onChange={(e) => setV(e.target.value)} />
          ) : (
            <>
              <EntradaSensivel mascara={null} livre nomesNoTexto={[o.parceiro_nome]} id={id} list={opcoes?.length ? `${id}-opcoes` : undefined} value={v} maxLength={200} aria-invalid={!!erro} onChange={(e) => setV(e.target.value)} />
              {!!opcoes?.length && (
                <datalist id={`${id}-opcoes`}>
                  {opcoes.map((x) => (
                    <option key={x} value={x} />
                  ))}
                </datalist>
              )}
            </>
          )}
        </Rotulo>
      )}
    </EdicaoRapida>
  );
}

function Contatos({ o, f, podeEditar, aoMudar }: { o: Oportunidade; f: Ficha; podeEditar: boolean; aoMudar: () => Promise<void> }) {
  // Rodada crm2 (SPEC-B2): adicionar contato busca no Cadastros; sem a leitura dele, o servidor recusa.
  const s = useSessao();
  const leCadastros = s.pode('cadastros.ver');
  const editaCadastro = s.instalado('cadastros') && s.pode('cadastros.editar');
  const Formulario = useFormularioParceiro();
  const [editar, setEditar] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [adicionando, setAdicionando] = useState(false);
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const acao = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: aoMudar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const copiar = (texto: string) => {
    void navigator.clipboard?.writeText(texto);
    toast.sucesso(t('crm.ficha.copiado'));
  };
  return (
    <div className="flex flex-col gap-2 py-1" data-contatos-negociacao="">
      {f.contatos.length === 0 && <p className="text-sm text-tinta-3">{t('crm.ficha.semContatos')}</p>}
      {f.contatos.map((c) => {
        const digitos = (c.telefone ?? '').replace(/\D/g, '');
        return (
          <div key={c.parceiro_id} className="rounded-lg border border-borda p-2 text-sm" data-contato={c.nome}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <LinkParceiro id={c.parceiro_id} nome={c.nome} className="font-semibold text-acento-forte hover:underline" para={CAMINHO_CONTATO_CRM(c.parceiro_id)} />
                {c.principal && <Selo cor="azul">{t('crm.ficha.principal')}</Selo>}
              </span>
              {podeEditar && (
                <span className="flex shrink-0">
                  {!c.principal && (
                    <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha.tornarPrincipal', { nome: nomePessoa(c.nome) })} title={t('crm.ficha.tornarPrincipal', { nome: nomePessoa(c.nome) })} icone={<Check className="size-4" />} onClick={() => acao.mutate(() => post(`/pipeline/oportunidades/${o.id}/contatos/${c.parceiro_id}/principal`))} />
                  )}
                  {Formulario && editaCadastro && (
                    <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha2.editarContato', { nome: nomePessoa(c.nome) })} title={t('crm.ficha2.editarContato', { nome: nomePessoa(c.nome) })} icone={<Pencil className="size-4" />} onClick={() => setEditar(c.parceiro_id)} data-editar-contato="" />
                  )}
                  <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha.removerContato', { nome: nomePessoa(c.nome) })} title={t('crm.ficha.removerContato', { nome: nomePessoa(c.nome) })} icone={<X className="size-4" />} onClick={() => acao.mutate(() => del(`/pipeline/oportunidades/${o.id}/contatos/${c.parceiro_id}`))} />
                </span>
              )}
            </div>
            {c.cargo && <p className="text-xs text-tinta-3">{textoLivre(c.cargo)}</p>}
            {c.telefone && (
              <p className="mt-1 flex items-center gap-1 text-xs">
                <span className="numero">{telefoneFormatado(c.telefone)}</span>
                <button type="button" className="rounded p-1 hover:bg-sup-3" aria-label={t('crm.ficha.copiar')} onClick={() => copiar(c.telefone!)}>
                  <Copy className="size-3.5" aria-hidden />
                </button>
                {digitos.length >= 10 && (
                  <a href={`https://wa.me/${digitos.length <= 11 ? `55${digitos}` : digitos}`} target="_blank" rel="noopener noreferrer" className="rounded p-1 text-verde hover:bg-sup-3" aria-label={t('crm.ficha.whatsapp')}>
                    <MessageCircle className="size-3.5" aria-hidden />
                  </a>
                )}
              </p>
            )}
            {c.email && <p className="mt-0.5 truncate text-xs" title={emailFmt(c.email)}>{emailFmt(c.email)}</p>}
            {leCadastros && <InfoAdicionalContato c={c} />}
          </div>
        );
      })}
      {podeEditar &&
        leCadastros &&
        (adicionando ? (
          <div className="flex flex-col gap-2 rounded-lg border border-borda bg-sup-2 p-2">
            <Rotulo id={`${id}-contato`} rotulo={t('crm.ficha.contatoExistente')}>
              <BuscaRemota id={`${id}-contato`} def={{ caminho: '/cadastros/parceiros', params: { tipo: 'pessoa' }, rotulo: (i) => String(i.nome), pessoa: true }} valor={escolhido} aoMudar={(v) => setEscolhido(v)} />
            </Rotulo>
            {Formulario && s.pode('cadastros.criar') && (
              <button type="button" className="flex w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline" onClick={() => setNovo(true)} data-criar-contato="">
                <Plus className="size-4" aria-hidden />
                {t('crm.ficha2.criarContato')}
              </button>
            )}
            <div className="flex justify-end gap-2">
              <Botao tamanho="sm" variante="fantasma" onClick={() => setAdicionando(false)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao
                tamanho="sm"
                variante="primario"
                disabled={!escolhido}
                data-adicionar-contato-confirmar=""
                onClick={() =>
                  acao.mutate(async () => {
                    await post(`/pipeline/oportunidades/${o.id}/contatos`, { parceiro_id: escolhido });
                    setAdicionando(false);
                    setEscolhido(null);
                  })
                }
              >
                {t('crm.ficha.adicionar')}
              </Botao>
            </div>
          </div>
        ) : (
          <button type="button" className="flex w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline" onClick={() => setAdicionando(true)} data-adicionar-contato="">
            <Plus className="size-4" aria-hidden />
            {t('crm.ficha.adicionarContato')}
          </button>
        ))}
      {/* Ajustes do CRM (27/10, F1): editar o contato e criar um novo (já posto na negociação) pelo formulário do Cadastros. */}
      {Formulario && editar && <Formulario tipo="pessoa" aberto id={editar} aoFechar={() => setEditar(null)} aoSalvar={() => void aoMudar()} />}
      {Formulario && novo && (
        <Formulario
          tipo="pessoa"
          aberto
          empresa={f.empresa?.tipo === 'empresa' ? { id: f.empresa.id, nome: f.empresa.nome } : null}
          aoFechar={() => setNovo(false)}
          aoSalvar={(nid) =>
            acao.mutate(async () => {
              await post(`/pipeline/oportunidades/${o.id}/contatos`, { parceiro_id: nid });
              setAdicionando(false);
            })
          }
        />
      )}
    </div>
  );
}

/** Lista de 30/10 (B), item 7.1: o responsável na edição rápida, como os outros campos (a lista só escolhe; grava no Salvar, R44). */
function Responsavel({ o, podeEditar, aoSalvar }: { o: Oportunidade; podeEditar: boolean; aoSalvar: () => Promise<void> }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const [escolhido, setEscolhido] = useState(o.responsavel_id ?? '');
  const [abriu, setAbriu] = useState(false);
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000, enabled: podeEditar && abriu });
  const opcoes = (membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }));
  if (o.responsavel_id && !opcoes.some((x) => x.valor === o.responsavel_id)) opcoes.unshift({ valor: o.responsavel_id, rotulo: nomePessoa(o.responsavel_nome) });
  return (
    <EdicaoRapida
      rotulo={t('campos.responsavel')}
      exibir={o.responsavel_nome ? nomePessoa(o.responsavel_nome) : ''}
      pode={podeEditar}
      aoAbrir={() => {
        setEscolhido(o.responsavel_id ?? '');
        setAbriu(true);
      }}
      aoSalvar={async () => {
        if (escolhido !== (o.responsavel_id ?? '')) await patch(`/pipeline/oportunidades/${o.id}`, { responsavel_id: escolhido || null });
        await aoSalvar();
      }}
      data-responsavel-ficha=""
    >
      {({ id, erro }) => (
        <Rotulo id={id} rotulo={t('campos.responsavel')} erro={erro || undefined}>
          <Selecao id={id} aria-invalid={!!erro} value={escolhido} vazio={tn('comum.selecione')} opcoes={opcoes} onChange={(e) => setEscolhido(e.target.value)} />
        </Rotulo>
      )}
    </EdicaoRapida>
  );
}

/** "Orientações por etapa" (o playbook do RD): objetivo, descrição e os itens da etapa, marcáveis na negociação. */
function Playbook({ o, f, podeEditar, aoFechar }: { o: Oportunidade; f: Ficha; podeEditar: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const [abertas, setAbertas] = useState<Set<string>>(new Set([o.etapa_id]));
  const marcar = useMutation({
    mutationFn: (v: { etapa_id: string; marcados: number[] }) => api('PUT', `/pipeline/oportunidades/${o.id}/playbook`, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline', 'crm', 'ficha', o.id] }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Drawer aberto aoFechar={aoFechar} largura="lg" titulo={t('crm.playbook.titulo')}>
      <p className="mb-3 text-sm text-tinta-2">{t('crm.playbook.ajuda')}</p>
      <ul className="flex flex-col gap-2" data-playbook="">
        {f.etapas
          .filter((e) => e.tipo === 'aberta')
          .map((e) => {
            const aberta = abertas.has(e.id);
            const marcados = f.playbook_marcados[e.id] ?? [];
            return (
              <li key={e.id} className="rounded-xl border border-borda" data-playbook-etapa={e.nome}>
                <button
                  type="button"
                  aria-expanded={aberta}
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold"
                  onClick={() =>
                    setAbertas((x) => {
                      const n = new Set(x);
                      if (n.has(e.id)) n.delete(e.id);
                      else n.add(e.id);
                      return n;
                    })
                  }
                >
                  {e.nome}
                  <ChevronDown className={`size-4 ${aberta ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {aberta && (
                  <div className="flex flex-col gap-3 border-t border-borda px-4 py-3 text-sm">
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-tinta-3 uppercase">{t('crm.playbook.objetivo')}</p>
                      <p className="whitespace-pre-wrap">{e.playbook.objetivo || t('crm.playbook.semObjetivo')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-tinta-3 uppercase">{t('crm.playbook.descricao')}</p>
                      <p className="whitespace-pre-wrap">{e.playbook.descricao ? textoLivre(e.playbook.descricao) : t('crm.playbook.semDescricao')}</p>
                    </div>
                    {!!e.playbook.itens?.length && (
                      <ul className="flex flex-col gap-1">
                        {e.playbook.itens.map((item, n) => (
                          <li key={n}>
                            <label className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                disabled={!podeEditar}
                                checked={marcados.includes(n)}
                                onChange={(ev) => marcar.mutate({ etapa_id: e.id, marcados: ev.target.checked ? [...marcados, n] : marcados.filter((x) => x !== n) })}
                              />
                              {item}
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
      </ul>
      <p className="mt-3 text-xs text-tinta-3">
        <Link to="/crm/configuracoes/funis" className="text-acento-forte underline">
          {t('crm.playbook.configurar')}
        </Link>
      </p>
    </Drawer>
  );
}
